import React from 'react';
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PRODUCT_TYPES } from '@schuettflix/interfaces';
import { SelectCombobox } from '@/shared/ui/Combobox/SelectCombobox.tsx';
import { PRODUCT_NAME_LABEL } from '@/phrase/ProductNameLabels.ts';

type ProductTypeFieldValues = { [key: string]: PRODUCT_TYPES };

export type ProductTypeInputProps = Pick<UseControllerProps<ProductTypeFieldValues>, 'defaultValue' | 'name'> & {
    productTypes: PRODUCT_TYPES[];
    placeholder?: string;
    disabled?: boolean;
    dataTest?: string;
    className?: string;
    onProductTypeChange?: (productType?: PRODUCT_TYPES) => void;
};

export const ProductTypeInput: React.FC<ProductTypeInputProps> = React.forwardRef<
    HTMLInputElement,
    ProductTypeInputProps
>(
    (
        {
            name = 'productType',
            defaultValue,
            productTypes,
            onProductTypeChange,
            placeholder,
            disabled,
            dataTest,
            className,
        },
        _ref
    ) => {
        const { control } = useFormContext();
        const { t } = useTranslation();

        const options = productTypes.map(productType => ({
            value: productType,
            label: t(PRODUCT_NAME_LABEL[productType]),
        }));

        return (
            <Controller
                control={control}
                defaultValue={defaultValue ?? null}
                name={name}
                render={({ field: { ref, value, onChange }, fieldState }) => (
                    <SelectCombobox
                        fieldRef={ref}
                        value={value ?? undefined}
                        onValueChange={productType => {
                            onProductTypeChange?.(productType);
                            onChange(productType);
                        }}
                        options={options}
                        disabled={disabled}
                        placeholder={placeholder}
                        error={fieldState.error?.message}
                        dataTest={dataTest}
                        className={className}
                    />
                )}
            />
        );
    }
);
